import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import supabase from '../services/supabaseClient';
import { parseUserAppAccess, DEFAULT_USER_APP_ACCESS } from '../utils/userAppAccess';
import {
  captureRecoveryIntentFromUrl,
  clearPasswordRecoveryPending,
  isPasswordRecoveryPending,
  markPasswordRecoveryPending,
} from '../utils/passwordReset';
import { getStaffLogoutUrl } from '../utils/portalAuth';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState(null);
  const [careHomeId, setCareHomeId] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [userAppAccess, setUserAppAccess] = useState(DEFAULT_USER_APP_ACCESS);
  const [userAppAccessLoaded, setUserAppAccessLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const lastSignInSignatureRef = useRef(null);
  const profileLoadedForUserRef = useRef(null);
  const [passwordRecoveryPending, setPasswordRecoveryPending] = useState(() => isPasswordRecoveryPending());

  useEffect(() => {
    let isMounted = true;
    captureRecoveryIntentFromUrl();
    setPasswordRecoveryPending(isPasswordRecoveryPending());

    const initializeAuth = async () => {
      try {
        const sessionResult = await Promise.race([
          supabase.auth.getSession(),
          new Promise((_, reject) => {
            window.setTimeout(() => reject(new Error('Auth session timeout')), 8000);
          }),
        ]);
        const session = sessionResult?.data?.session;
        
        if (!isMounted) return;

        if (session?.user) {
          setUser(session.user);
          
          // Use user_metadata for role/name (instantly available, no DB query)
          const userRole = session.user?.user_metadata?.role || 'staff';
          const userName = session.user?.user_metadata?.name || session.user?.email?.split('@')?.[0] || 'User';
          const userCareHome = session.user?.user_metadata?.care_home_id || null;
          const userOrganization = session.user?.user_metadata?.organization_id || null;
          
          setRole(userRole);
          setDisplayName(userName);
          setCareHomeId(userCareHome);
          setOrganizationId(userOrganization);
          
          // Fetch full profile async in background (won't block loading)
          supabase
            .from('user_profiles')
            .select('role, name, username, care_home_id, organization_id, status, app_access, onboarding_completed, must_change_password')
            .eq('id', session.user.id)
            .single()
            .then(({ data: profile, error }) => {
              if (!isMounted) return;
              
              if (profile && !error) {
                setUserAppAccess(parseUserAppAccess(profile.app_access));
                // Update with actual DB values if different
                if (profile.role && profile.role !== userRole) {
                  setRole(profile.role);
                }
                if (profile.name && profile.name !== userName) {
                  setDisplayName(profile.name);
                }
                if (profile.care_home_id && profile.care_home_id !== userCareHome) {
                  setCareHomeId(profile.care_home_id);
                }
                if (profile.organization_id && profile.organization_id !== userOrganization) {
                  setOrganizationId(profile.organization_id);
                }
                // Store username
                if (profile.username) {
                  setUsername(profile.username);
                }
                // Store user status
                if (profile.status) {
                  setUserStatus(profile.status);
                }
                setOnboardingCompleted(profile.onboarding_completed !== false);
                setMustChangePassword(profile.must_change_password === true);
                profileLoadedForUserRef.current = session.user.id;
              }
              setProfileLoaded(true);
              setUserAppAccessLoaded(true);
              // Mark status as loaded even on error to avoid infinite loading
              setStatusLoaded(true);
            })
            .catch(err => {
              console.warn('Background profile fetch failed:', err);
              setUserAppAccess(DEFAULT_USER_APP_ACCESS);
              setProfileLoaded(true);
              setUserAppAccessLoaded(true);
              setStatusLoaded(true);
            });
          
          setIsLoading(false);
        } else {
          setUser(null);
          setRole(null);
          setDisplayName('');
          setUsername(null);
          setCareHomeId(null);
          setUserAppAccess(DEFAULT_USER_APP_ACCESS);
          setUserAppAccessLoaded(false);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error getting session:', error);
        if (isMounted) {
          setUser(null);
          setRole(null);
          setDisplayName('');
          setCareHomeId(null);
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;

      if (event === 'PASSWORD_RECOVERY') {
        markPasswordRecoveryPending();
        setPasswordRecoveryPending(true);
      }
      if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
        clearPasswordRecoveryPending();
        setPasswordRecoveryPending(false);
      }

      if (session?.user) {
        const signInSignature = `${session.user.id}:${session.access_token || 'no-token'}`;
        if (event === 'SIGNED_IN' && lastSignInSignatureRef.current === signInSignature) {
          // Supabase can emit duplicate SIGNED_IN events on tab-focus rehydrate.
          setIsLoading(false);
          setStatusLoaded(true);
          return;
        }
        if (event === 'SIGNED_IN') {
          lastSignInSignatureRef.current = signInSignature;
        }

        // Prevent unnecessary context churn on token refresh when user identity hasn't changed.
        setUser((prev) => {
          if (!prev) return session.user;
          if (prev.id !== session.user.id) return session.user;
          if (event === 'USER_UPDATED') return session.user;
          return prev;
        });

        // Token refresh should not rewrite role/profile-derived state or trigger DB reads.
        if (event === 'TOKEN_REFRESHED') {
          setIsLoading(false);
          setStatusLoaded(true);
          return;
        }
        
        // Use user_metadata for role/name (instantly available, no DB query)
        const userRole = session.user?.user_metadata?.role || 'staff';
        const userName = session.user?.user_metadata?.name || session.user?.email?.split('@')?.[0] || 'User';
        const userCareHome = session.user?.user_metadata?.care_home_id || null;
        const userOrganization = session.user?.user_metadata?.organization_id || null;
        
        setRole(userRole);
        setDisplayName(userName);
        setCareHomeId(userCareHome);
        setOrganizationId(userOrganization);
        setIsLoading(false);

        // If profile has already been loaded for this user, skip repeated DB reads.
        if (profileLoadedForUserRef.current === session.user.id && event !== 'USER_UPDATED') {
          setUserAppAccessLoaded(true);
          setStatusLoaded(true);
          return;
        }
        
        supabase
          .from('user_profiles')
          .select('role, name, username, care_home_id, organization_id, status, app_access, onboarding_completed, must_change_password')
          .eq('id', session.user.id)
          .single()
          .then(({ data: profile, error }) => {
            if (!isMounted) return;

            if (profile && !error) {
              setUserAppAccess(parseUserAppAccess(profile.app_access));
              // Update with actual DB values if different
              if (profile.role && profile.role !== userRole) {
                setRole(profile.role);
              }
              if (profile.name && profile.name !== userName) {
                setDisplayName(profile.name);
              }
              if (profile.care_home_id && profile.care_home_id !== userCareHome) {
                setCareHomeId(profile.care_home_id);
              }
              if (profile.organization_id && profile.organization_id !== userOrganization) {
                setOrganizationId(profile.organization_id);
              }
              // Store username
              if (profile.username) {
                setUsername(profile.username);
              }
              // Store user status
              if (profile.status) {
                setUserStatus(profile.status);
              }
              setOnboardingCompleted(profile.onboarding_completed !== false);
              setMustChangePassword(profile.must_change_password === true);
              profileLoadedForUserRef.current = session.user.id;
            }
            setProfileLoaded(true);
            setUserAppAccessLoaded(true);
            // Mark status as loaded even on error to avoid infinite loading
            setStatusLoaded(true);
          })
          .catch(err => {
            console.warn('Auth change - Background profile fetch failed:', err);
            setUserAppAccess(DEFAULT_USER_APP_ACCESS);
            setProfileLoaded(true);
            setUserAppAccessLoaded(true);
            setStatusLoaded(true);
          });
      } else {
        // Only clear auth state on explicit sign-out style events.
        // Some transient auth events can surface a null session briefly during refresh.
        if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          setUser(null);
          setRole(null);
          setDisplayName('');
          setUsername(null);
          setCareHomeId(null);
          setOrganizationId(null);
          setUserStatus(null);
          setProfileLoaded(false);
          setOnboardingCompleted(true);
          setMustChangePassword(false);
          setUserAppAccess(DEFAULT_USER_APP_ACCESS);
          setUserAppAccessLoaded(false);
          lastSignInSignatureRef.current = null;
          profileLoadedForUserRef.current = null;
          setStatusLoaded(false);
          setIsLoading(false);
        } else {
          console.warn('Auth change - Ignoring transient null session event:', event);
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return data;
  };

  const logout = async () => {
    const dest = getStaffLogoutUrl();

    // Local sign-out only. A global revoke can finish after the next login and
    // kill that session, which leaves the portal spinner stuck.
    if (supabase) {
      try {
        await Promise.race([
          supabase.auth.signOut({ scope: 'local' }),
          new Promise((resolve) => { window.setTimeout(resolve, 2000); }),
        ]);
      } catch (err) {
        console.warn('supabase.signOut threw error:', err);
      }
    }

    // Robust cleanup: remove any Supabase auth keys that may remain in localStorage
    try {
      Object.keys(localStorage).forEach((key) => {
        if (typeof key === 'string' && (
          key.startsWith('supabase') || key.includes('supabase') || key.startsWith('sb:') || key.includes('supa') || key.includes('sb-')
        )) {
          try { localStorage.removeItem(key); } catch { /* ignore */ }
        }
      });
      // Also try a broad remove of commonly used keys
      ['supabase.auth.token', 'supabase.auth', 'sb-access-token', 'sb-refresh-token'].forEach(k => { try { localStorage.removeItem(k); } catch {} });
      // Broadcast sign-out to other tabs
      try { localStorage.setItem('app:signed_out', String(Date.now())); } catch {}
    } catch (e) {
      console.warn('Failed clearing localStorage auth keys:', e);
    }

    // Also clear cookies (best-effort) since some environments may persist tokens there
    try {
      document.cookie.split(';').forEach(function(c) {
        const name = c.split('=')[0].trim();
        // expire cookie
        document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/';
      });
    } catch (e) {
      // ignore cookie clearing errors
    }

    // Cross-origin Portal logout: hard-redirect BEFORE setUser(null).
    // Clearing user first mounts StaffLoginRedirect, which races to
    // app.maap.care/?next=… (no signed_out) and lands on /apps while
    // Portal still has a local session.
    if (typeof window !== 'undefined' && /^https?:\/\//i.test(dest)) {
      try {
        sessionStorage.setItem('maap:staff_logging_out', '1');
      } catch {
        /* ignore */
      }
      window.location.replace(dest);
      return;
    }

    // Reset in-memory state (same-origin /login only)
    setUser(null);
    setRole(null);
    setDisplayName('');
    setUsername(null);
    setCareHomeId(null);
    setUserStatus(null);
    setProfileLoaded(false);
    setOnboardingCompleted(true);
    setMustChangePassword(false);
    setUserAppAccess(DEFAULT_USER_APP_ACCESS);
    setUserAppAccessLoaded(false);
    setStatusLoaded(false);
    clearPasswordRecoveryPending();
    setPasswordRecoveryPending(false);

    try {
      window.location.replace(dest);
    } catch (err) {
      try { window.location.reload(); } catch { /* ignore */ }
    }
  };

  const isSuperAdmin = role === 'Super Admin';

  const value = {
    user,
    role,
    displayName,
    updateDisplayName: (name) => setDisplayName(String(name || '').trim()),
    username,
    setUsername,
    careHomeId,
    organizationId,
    userStatus,
    statusLoaded,
    mustChangePassword,
    onboardingLoaded: isSuperAdmin || profileLoaded,
    onboardingRequired: !isSuperAdmin && profileLoaded && onboardingCompleted === false,
    userAppAccess,
    userAppAccessLoaded,
    isLoading,
    passwordRecoveryPending,
    login,
    logout,
    isAdmin: role === 'admin' || role === 'Super Admin' || role === 'Organization Admin',
    isSuperAdmin: role === 'Super Admin',
    isOrgAdmin: role === 'Organization Admin' || role === 'admin',
    isCareHomeManager: role === 'Care Home Manager' || role === 'care_home_manager',
    // Default to active unless explicitly marked inactive
    isActive: userStatus !== 'Inactive',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
